import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { CalendarPlus, Calendar, Clock, Trash2, Check, X, Pencil, MessageCircle, Phone, Scissors, Mail, ClipboardCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { useAuth } from "../../context/AuthContext";
import SurgeryNotes from "./SurgeryNotes";

const TYPES = [
  { v: "primera_vez", l: "Primera vez" },
  { v: "seguimiento", l: "Seguimiento" },
  { v: "vacunacion", l: "Vacunación" },
  { v: "cirugia", l: "Cirugía" },
];
const TYPE_LABEL = Object.fromEntries(TYPES.map((t) => [t.v, t.l]));

const STATUS = {
  programada: { l: "Programada", cls: "bg-brand-sage/15 text-brand-sageDark" },
  completada: { l: "Completada", cls: "bg-brand-ink/10 text-brand-ink" },
  cancelada: { l: "Cancelada", cls: "bg-red-100 text-red-600" },
};

const fmt = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-MX", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

const toLocalInput = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};

// Construye un enlace wa.me con el mensaje prellenado para enviar desde el WhatsApp del médico.
const waDigits = (phone) => {
  if (!phone) return null;
  let d = (phone + "").replace(/\D/g, "");
  if (!d) return null;
  if (d.length === 10) d = "52" + d;        // móvil MX local
  else if (d.startsWith("521")) d = d;      // ya trae lada WhatsApp MX
  else if (d.startsWith("52") && d.length === 12) d = d; // +52 + 10
  return d;
};

export default function AppointmentsPanel({ patient }) {
  const { user } = useAuth();
  const doctorName = user?.name || "su médico";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ scheduled_at: toLocalInput(), reason: "", type: "seguimiento", notes: "" });
  const [saving, setSaving] = useState(false);
  const [surgeryOpen, setSurgeryOpen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/patients/${patient.patient_id}/appointments`);
      setItems(r.data);
    } catch { /* noop */ } finally { setLoading(false); }
  }, [patient.patient_id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const h = () => load();
    window.addEventListener("clinped:appointments-updated", h);
    return () => window.removeEventListener("clinped:appointments-updated", h);
  }, [load]);

  const openNew = () => { setEditing(null); setForm({ scheduled_at: toLocalInput(), reason: "", type: "seguimiento", notes: "", email: patient.email || "" }); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ scheduled_at: toLocalInput(a.scheduled_at), reason: a.reason || "", type: a.type || "seguimiento", notes: a.notes || "" }); setOpen(true); };

  const save = async () => {
    if (!form.scheduled_at) { toast.error("Elige fecha y hora"); return; }
    setSaving(true);
    const { email, ...rest } = form;
    const payload = { ...rest, scheduled_at: new Date(form.scheduled_at).toISOString() };
    try {
      if (editing) await api.put(`/appointments/${editing.appointment_id}`, { ...payload, tz: Intl.DateTimeFormat().resolvedOptions().timeZone });
      else await api.post("/appointments", { patient_id: patient.patient_id, ...payload, email, tz: Intl.DateTimeFormat().resolvedOptions().timeZone });
      if (email) patient.email = email;
      const moved = editing && new Date(editing.scheduled_at).getTime() !== new Date(payload.scheduled_at).getTime();
      const to = editing ? (moved ? patient.email : null) : email;
      toast.success(editing ? "Cita actualizada" : "Cita agendada", { description: to ? `Enviaremos la confirmación a ${to}` : undefined });
      setTimeout(load, 4000);
      setOpen(false); load();
    } catch { toast.error("No se pudo guardar la cita"); } finally { setSaving(false); }
  };

  const setStatus = async (a, status) => {
    try { await api.put(`/appointments/${a.appointment_id}`, { status }); load(); }
    catch { toast.error("Error al actualizar"); }
  };
  const remove = async (a) => {
    if (!window.confirm("¿Eliminar esta cita?")) return;
    try { await api.delete(`/appointments/${a.appointment_id}`); load(); }
    catch { toast.error("Error al eliminar"); }
  };

  const hasPhone = patient.guardian_phone || patient.phone;
  const waNum = waDigits(hasPhone);

  const waHref = (a) => {
    if (!waNum) return null;
    const who = patient.guardian_name ? `${patient.guardian_name}, ` : "";
    const msg = `Hola ${who}le recordamos la cita de ${patient.name} con ${doctorName} en ClinPed para el ${fmt(a.scheduled_at)}.` +
      (a.reason ? ` Motivo: ${a.reason}.` : "") +
      ` Si necesita reprogramar, por favor avísenos. ¡Gracias!`;
    return `https://wa.me/${waNum}?text=${encodeURIComponent(msg)}`;
  };

  return (
    <div className="bg-white rounded-3xl p-5 sm:p-6 shadow-[0_8px_30px_rgb(61,64,91,0.04)] border border-stone-100">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h3 className="font-outfit font-bold text-lg text-brand-ink flex items-center gap-2"><Calendar size={18} className="text-brand-sage" /> Agenda del paciente</h3>
          <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1 flex-wrap">
            <MessageCircle size={12} /> Envía el recordatorio al tutor por WhatsApp con un toque (desde tu propio WhatsApp)
            {hasPhone ? <span className="text-brand-sageDark font-semibold flex items-center gap-1"><Phone size={11} /> {hasPhone}</span>
              : <span className="text-amber-600 font-semibold">· agrega un teléfono del tutor en la ficha</span>}
          </p>
        </div>
        <button data-testid="new-appointment" onClick={openNew}
          className="flex items-center gap-1.5 bg-brand-terracotta text-white text-sm font-semibold px-4 py-2 rounded-xl hover:bg-brand-terracottaDark transition-all">
          <CalendarPlus size={16} /> Agendar cita
        </button>
      </div>

      {loading ? (
        <div className="h-32 flex items-center justify-center"><div className="w-7 h-7 rounded-full border-[3px] border-brand-sage/30 border-t-brand-terracotta animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="py-10 text-center text-muted-foreground text-sm" data-testid="no-appointments">Sin citas. Agenda la primera con el botón de arriba.</div>
      ) : (
        <div className="space-y-2.5" data-testid="appointments-list">
          {items.map((a) => {
            const st = STATUS[a.status] || STATUS.programada;
            const past = new Date(a.scheduled_at) < new Date();
            return (
              <motion.div key={a.appointment_id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                data-testid={`appointment-${a.appointment_id}`}
                className={`rounded-2xl border p-3.5 ${a.status === "cancelada" ? "border-stone-100 opacity-60" : "border-stone-100"}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="font-outfit font-semibold text-brand-ink text-sm flex items-center gap-1.5 capitalize">
                      <Clock size={14} className="text-brand-sage shrink-0" /> {fmt(a.scheduled_at)}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 mt-1.5">
                      <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-brand-sand/40 text-amber-700">{TYPE_LABEL[a.type] || a.type}</span>
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${st.cls}`}>{st.l}</span>
                      {a.confirmation_sent_at && <span data-testid={`appt-mailed-${a.appointment_id}`} className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 flex items-center gap-1"><Mail size={11} /> Correo enviado</span>}
                      {a.attendance_confirmed_at && <span data-testid={`appt-confirmed-${a.appointment_id}`} className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-brand-ink text-white">Confirmó asistencia</span>}
                      {a.cancelled_by === "paciente" && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-600">Canceló el paciente</span>}
                      {a.intake_completed_at && <span data-testid={`appt-intake-${a.appointment_id}`} className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 flex items-center gap-1"><ClipboardCheck size={11} /> Pre-registro listo</span>}
                    </div>
                    {a.intake_reason && <p className="text-xs text-emerald-700 mt-1"><b>Motivo (paciente):</b> {a.intake_reason}</p>}
                    {a.reason && <p className="text-sm text-brand-ink/80 mt-1.5"><b>Motivo:</b> {a.reason}</p>}
                    {a.notes && <p className="text-xs text-muted-foreground mt-0.5">{a.notes}</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {a.status === "programada" && (
                      waNum ? (
                        <a data-testid={`whatsapp-${a.appointment_id}`} href={waHref(a)} target="_blank" rel="noopener noreferrer" title="Recordar por WhatsApp"
                          className="flex items-center gap-1 text-[11px] font-bold text-white bg-[#25D366] hover:opacity-90 px-2.5 py-1.5 rounded-lg transition-all"><MessageCircle size={14} /> WhatsApp</a>
                      ) : (
                        <button data-testid={`whatsapp-${a.appointment_id}`} onClick={() => toast.error("Agrega un teléfono del tutor en la ficha del paciente")} title="Recordar por WhatsApp"
                          className="flex items-center gap-1 text-[11px] font-bold text-white bg-[#25D366]/60 px-2.5 py-1.5 rounded-lg transition-all"><MessageCircle size={14} /> WhatsApp</button>
                      )
                    )}
                    {a.status === "programada" && !past && (
                      <button data-testid={`complete-${a.appointment_id}`} onClick={() => setStatus(a, "completada")} title="Marcar completada"
                        className="p-2 rounded-lg text-brand-sageDark hover:bg-brand-sage/10 transition-all"><Check size={16} /></button>
                    )}
                    {a.status === "programada" && (
                      <button data-testid={`cancel-${a.appointment_id}`} onClick={() => setStatus(a, "cancelada")} title="Cancelar"
                        className="p-2 rounded-lg text-amber-600 hover:bg-amber-50 transition-all"><X size={16} /></button>
                    )}
                    <button data-testid={`edit-${a.appointment_id}`} onClick={() => openEdit(a)} title="Editar"
                      className="p-2 rounded-lg text-brand-ink/60 hover:bg-muted transition-all"><Pencil size={15} /></button>
                    <button data-testid={`delete-${a.appointment_id}`} onClick={() => remove(a)} title="Eliminar"
                      className="p-2 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all"><Trash2 size={15} /></button>
                  </div>
                </div>
                {a.type === "cirugia" && (
                  <div className="mt-2 pt-2 border-t border-stone-100">
                    <button data-testid={`surgery-toggle-${a.appointment_id}`}
                      onClick={() => setSurgeryOpen(surgeryOpen === a.appointment_id ? null : a.appointment_id)}
                      className="flex items-center gap-1.5 text-xs font-bold text-brand-sageDark hover:text-brand-sage transition-colors">
                      <Scissors size={14} /> {surgeryOpen === a.appointment_id ? "Ocultar" : "Notas quirúrgicas (pre / post / indicaciones)"}
                    </button>
                    {surgeryOpen === a.appointment_id && <SurgeryNotes appointment={a} patient={patient} />}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="appointment-dialog">
          <DialogHeader><DialogTitle className="font-outfit text-brand-ink">{editing ? "Editar cita" : "Nueva cita"}</DialogTitle></DialogHeader>
          <div className="space-y-3.5 mt-1">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Fecha y hora</Label>
              <Input data-testid="appt-datetime" type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} className="rounded-xl mt-1.5" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Tipo de cita</Label>
              <div className="flex bg-muted rounded-full p-1 mt-1.5">
                {TYPES.map((t) => (
                  <button key={t.v} type="button" data-testid={`appt-type-${t.v}`} onClick={() => setForm({ ...form, type: t.v })}
                    className={`flex-1 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${form.type === t.v ? "bg-brand-terracotta text-white" : "text-brand-ink/60"}`}>{t.l}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Motivo</Label>
              <Input data-testid="appt-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="rounded-xl mt-1.5" placeholder="Control de niño sano, revisión, etc." />
            </div>
            {!editing && (
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Correo del paciente o tutor</Label>
                <Input data-testid="appt-email" type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl mt-1.5" placeholder="Recibirá confirmación y recordatorio 24 h antes" />
              </div>
            )}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Notas</Label>
              <Textarea data-testid="appt-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-xl mt-1.5" rows={2} />
            </div>
            <button data-testid="appt-save" onClick={save} disabled={saving}
              className="w-full bg-brand-terracotta hover:bg-brand-terracottaDark text-white font-outfit font-semibold py-3 rounded-2xl transition-all disabled:opacity-60">
              {saving ? "Guardando…" : editing ? "Guardar cambios" : "Agendar cita"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
